import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Box, Button, Flex } from 'src/components/ui';

import Overlay from './Overlay';
import FusionLoading from './FusionLoading';
import ContentPendingProgress from './ContentPendingProgress';
import { isArray } from 'lodash';

interface PhotoEditorProps {
	input: {
		files: string[];
		resources?: string[];
	};
	isEditorLoading: boolean;
	onLoadingFinished: () => void;
	onSave: (file: ArrayBuffer) => void;
	onDownload: (file: ArrayBuffer) => void;
	onDocumentCount: (count: number) => void;
	showOverlay?: boolean;
	isSubmitting: boolean;
	showPendingProgress: boolean;
	onPendingProgressFinished: (val: boolean) => void;
	handleClearErrors: () => void;
	headlineText?: any;
	fonts?: string[];
}

const PhotoEditor: FC<PhotoEditorProps> = ({
	input,
	isEditorLoading,
	onLoadingFinished,
	onDocumentCount,
	onSave,
	onDownload,
	showOverlay,
	isSubmitting,
	showPendingProgress,
	onPendingProgressFinished,
	handleClearErrors,
	headlineText,
	fonts,
}) => {
	const [eventCounts, setEventCounts] = useState(0);
	const [eventName, setEventName] = useState('');
	const ppRef = useRef<any>(null);
	const resources = fonts?.length ? fonts : [];
	const DEFAULT_EDITOR_SETTINGS = {
		resources,
		environment: {
			theme: 2,
			panels: [2],
			menus: [],
			topt: { t0: [1, 1, 0] },
		},
	};

	const combinedInput = {
		...DEFAULT_EDITOR_SETTINGS,
		...input,
	};

	const parsedInput = encodeURIComponent(JSON.stringify(combinedInput));
	const photopeaUrl = `https://www.photopea.com#${parsedInput}`;

	useEffect(() => {
		if (isEditorLoading && eventCounts >= 1) onLoadingFinished();
	}, [eventCounts, isEditorLoading]);

	useEffect(() => {
		window.addEventListener('message', handleEvents);
		return () => window.removeEventListener('message', handleEvents);
	}, []);

	const eventNameRef = useRef(eventName);
	useEffect(() => {
		eventNameRef.current = eventName;
	}, [eventName]);

	const handleEvents = (e: MessageEvent) => {
		if (e.data === 'done') setEventCounts((prev) => prev + 1);

		const isSaveAction = e.data instanceof ArrayBuffer;
		if (isSaveAction) {
			eventNameRef.current === 'save' && onSave(e.data);
			eventNameRef.current === 'download' && onDownload(e.data);
			setEventName('');
		} else if (
			typeof e?.data === 'string' &&
			(e.data as string)?.indexOf('documentCount') === 0
		) {
			const documentCount = Number(e.data.split('=').pop());
			onDocumentCount(documentCount);
		}
	};

	const handleLoadFile = async (event: ChangeEvent<HTMLInputElement>) => {
		const pp = ppRef.current?.contentWindow;
		if (!pp) return;

		pp.postMessage(
			'app.activeDocument.close(SaveOptions.DONOTSAVECHANGES)',
			'*',
		);

		const file = event.target.files?.[0];
		if (!file) return;

		const buffer = await file.arrayBuffer();
		pp.postMessage(buffer, '*');
	};

	const handleTriggerSave = (action: string) => {
		setEventName(action);
		handleClearErrors();
		const pp = ppRef.current?.contentWindow;
		if (!pp) return;

		pp.postMessage('app.activeDocument.saveToOE("psd");', '*');
	};

	useEffect(() => {
		const intervalId = setInterval(handleCountDocuments, 500);
		return () => clearInterval(intervalId);
	}, [isEditorLoading]);

	const handleCountDocuments = () => {
		const pp = ppRef.current?.contentWindow;
		if (!pp) return;

		pp.postMessage(
			'app.echoToOE("documentCount="+(app.documents.length));',
			'*',
		);
	};

	const handleChangeHeadline = async () => {
		const pp = ppRef.current?.contentWindow;
		if (!pp || !headlineText) return;
		if (isArray(headlineText)) {
			headlineText.forEach((layer) => {
				let groups = '';
				let previousGroup = 'app.activeDocument';
				layer.parents.forEach((parent: string, index: number) => {
					groups += `
					var group${index} = ${previousGroup}.layerSets.getByName('${parent}');
			`;
					previousGroup = `group${index}`;
				});
				pp.postMessage(
					`${groups}
					var textLayer = ${previousGroup}.layers.getByName('${layer.name}');
					textLayer.textItem.contents = '${layer.content
						.replace(/'/g, "\\'")
						.replace(/\n/g, '\\r')}';
					`,
					'*',
				);
			});
		}
	};

	return (
		<Box w="full" h="650px" pos="relative">
			<input
				type="file"
				accept=".psd"
				onChange={handleLoadFile}
				style={{ display: 'none' }}
				id="file-input"
			/>
			{showOverlay && (
				<Overlay zIndex={50} h="full" bg="rgba(255, 255, 255, 0.5)">
					<Flex
						w="full"
						h="full"
						pos="relative"
						zIndex={51}
						justify="center"
						align="center"
					>
						<FusionLoading isLoading={showOverlay} />
					</Flex>
					<div
						style={{
							position: 'absolute',
							top: '0px',
							left: '0px',
							width: '100%',
							height: '100%',
							backgroundColor: 'rgb(155, 156, 161)',
							zIndex: 49,
						}}
					/>
				</Overlay>
			)}

			{showPendingProgress && (
				<Overlay zIndex={50} w="full" h="full" bg="rgba(105, 105, 105, 0.5)">
					<Flex w="full" h="full" justify="center" align="center">
						<Flex w="75%" h="full" justify="center" align="center">
							<ContentPendingProgress
								time={30}
								isPendingGeneration={isSubmitting}
								onGenerateStatusChange={(val) => {
									onPendingProgressFinished(val);
								}}
								progressText="Saving"
								isGray
							/>
						</Flex>
					</Flex>
				</Overlay>
			)}
			<iframe src={photopeaUrl} width="100%" height="100%" ref={ppRef}></iframe>
			<Button
				onClick={() => handleTriggerSave('save')}
				display="none"
				id="psd-save-button"
			>
				Save
			</Button>
			<Button
				onClick={() => handleTriggerSave('download')}
				display="none"
				id="psd-download-button"
			>
				download
			</Button>
			<Button
				onClick={handleChangeHeadline}
				display="none"
				id="change-text-layer-button-edit-mode"
			/>
		</Box>
	);
};

export default PhotoEditor;
